'use no memo';

import { useEffect, useEffectEvent, useState } from 'react';

import { DEFAULT_LOCALE, getPrefix } from '#src/lib/i18n.ts';
import {
  getAllowedLocales,
  getSafeLocaleFromUrl,
} from '#src/lib/navigation/getSafeLocaleFromUrl.ts';
import { localStorage } from '#src/lib/storage/localStorage.ts';

/**
 * This redirects to the active language
 * according to the following algo:
 *
 * 1. If the locale is set in url use that
 * 2. Fallback to the language picked explicitly
 * 3. Fallback to first browser locale that is supported
 * 4. Fallback to english
 *
 * English lives on the unprefixed url: /en/* is replaced by /*.
 */
export const useBasenameLocale = () => {
  // oxlint-disable-next-line no-restricted-properties
  const localeFromUrl = window.location.pathname.split('/')[1] ?? '';
  const prefix =
    localeFromUrl.length > 5 || localeFromUrl.length < 2
      ? undefined
      : getPrefix(localeFromUrl ?? '');

  const [, updateState] = useState({});
  const forceRerender = () => updateState({});

  // oxlint-disable-next-line no-restricted-properties
  const safeLocale = getSafeLocaleFromUrl(window.location.pathname);
  const localeInitialized =
    prefix !== '/en' &&
    (safeLocale === DEFAULT_LOCALE || prefix === `/${localeFromUrl}`);

  const getPrefixFromLocale = (locale: string | null) => {
    const newAllowedLocale = getAllowedLocales(locale);
    if (!newAllowedLocale) return null;
    if (newAllowedLocale === DEFAULT_LOCALE) return '';
    return getPrefix(newAllowedLocale) ?? null;
  };

  const replaceHistoryState = (newPrefix: string) => {
    // oxlint-disable-next-line no-restricted-properties
    const { pathname, search, hash } = window.location;
    // oxlint-disable-next-line no-restricted-properties
    window.history.replaceState(
      null,
      '',
      `${newPrefix}${prefix ? pathname.replace(/^\/[^/]+/, '') || '/' : pathname}${search}${hash}`
    );
    // force rerender to signal the prefix update
    forceRerender();
  };
  const setLocale = (locale: string | null) => {
    const newPrefix = getPrefixFromLocale(locale);
    if (newPrefix !== null) {
      localStorage.setItem('preferredLocale', locale);
      replaceHistoryState(newPrefix);
    }
  };

  const initializeLocale = useEffectEvent(() => {
    if (prefix === '/en') {
      setLocale(DEFAULT_LOCALE);
      return;
    }
    const newPrefix = getPrefixFromLocale(safeLocale);
    if (newPrefix !== null) {
      replaceHistoryState(newPrefix);
    }
  });

  useEffect(() => {
    if (localeInitialized) {
      return;
    }
    // oxlint-disable-next-line react/set-state-in-effect
    initializeLocale();
  }, [localeInitialized]);

  return {
    locale: safeLocale,
    setLocale,
    basename: prefix,
    loading: !localeInitialized,
  };
};
